/**
 * Section Studio — slide-over panel that opens when a section is selected.
 * Shows the per-section version history + the AI variant picker (Phase #2).
 *
 * Wired against the editor section-versions API:
 *   GET    /editor/:projectId/:jobId/sections/:sectionId/versions
 *   POST   /editor/:projectId/:jobId/sections/:sectionId/versions
 *   POST   /editor/:projectId/:jobId/sections/:sectionId/versions/:versionId/revert
 *   POST   /editor/:projectId/:jobId/sections/:sectionId/variants
 *   POST   /editor/:projectId/:jobId/sections/:sectionId/variants/:batchId/apply
 */

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, RotateCcw, Save, Sparkles, Loader2, Check, History, Wand2, Clock } from 'lucide-react';
import api from '../../lib/api';

interface SectionVersion {
  versionId: string;
  sectionId: string;
  createdAt: string;
  source: 'auto' | 'manual' | 'chat' | 'variant' | 'revert';
  label?: string;
  content: Record<string, unknown>;
}

interface SectionVariant {
  variantId: string;
  hint: string;
  content: Record<string, unknown>;
}

interface SectionVariantBatch {
  batchId: string;
  sectionId: string;
  createdAt: string;
  hints: string[];
  variants: SectionVariant[];
  costUsd: number;
}

interface SectionStudioProps {
  projectId: string;
  jobId: string;
  sectionId: string;
  onClose: () => void;
  onContentUpdated: () => void;
}

const SOURCE_BADGE: Record<SectionVersion['source'], { label: string; cls: string }> = {
  auto: { label: 'auto', cls: 'bg-gray-100 text-gray-600' },
  manual: { label: 'saved', cls: 'bg-blue-100 text-blue-700' },
  chat: { label: 'chat', cls: 'bg-purple-100 text-purple-700' },
  variant: { label: 'variant', cls: 'bg-amber-100 text-amber-700' },
  revert: { label: 'revert', cls: 'bg-rose-100 text-rose-700' },
};

export function SectionStudio({
  projectId,
  jobId,
  sectionId,
  onClose,
  onContentUpdated,
}: SectionStudioProps) {
  const [tab, setTab] = useState<'history' | 'variants'>('history');
  const [batch, setBatch] = useState<SectionVariantBatch | null>(null);

  // ----- versions ---------------------------------------------------------
  const versionsQuery = useQuery({
    queryKey: ['section-versions', projectId, jobId, sectionId],
    queryFn: async () => {
      const res = await api.get(`/editor/${projectId}/${jobId}/sections/${sectionId}/versions`);
      return (res.data.data?.versions ?? []) as SectionVersion[];
    },
  });

  const snapshotMutation = useMutation({
    mutationFn: async (label?: string) => {
      const res = await api.post(`/editor/${projectId}/${jobId}/sections/${sectionId}/versions`, {
        label,
      });
      return res.data.data;
    },
    onSuccess: () => {
      toast.success('Snapshot saved');
      versionsQuery.refetch();
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) =>
      toast.error(err.response?.data?.error?.message || 'Failed to snapshot'),
  });

  const revertMutation = useMutation({
    mutationFn: async (versionId: string) => {
      const res = await api.post(
        `/editor/${projectId}/${jobId}/sections/${sectionId}/versions/${versionId}/revert`
      );
      return res.data.data;
    },
    onSuccess: () => {
      toast.success('Reverted');
      versionsQuery.refetch();
      onContentUpdated();
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) =>
      toast.error(err.response?.data?.error?.message || 'Revert failed'),
  });

  // ----- variants ---------------------------------------------------------
  const generateMutation = useMutation({
    mutationFn: async (count: number) => {
      const res = await api.post(`/editor/${projectId}/${jobId}/sections/${sectionId}/variants`, {
        count,
      });
      return res.data.data as SectionVariantBatch;
    },
    onSuccess: (data) => {
      setBatch(data);
      toast.success(`Generated ${data.variants.length} variants`);
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) =>
      toast.error(err.response?.data?.error?.message || 'Variant generation failed'),
  });

  const applyMutation = useMutation({
    mutationFn: async (variantId: string) => {
      if (!batch) throw new Error('No batch loaded');
      const res = await api.post(
        `/editor/${projectId}/${jobId}/sections/${sectionId}/variants/${batch.batchId}/apply`,
        { variantId }
      );
      return res.data.data;
    },
    onSuccess: () => {
      toast.success('Variant applied');
      versionsQuery.refetch();
      onContentUpdated();
      setBatch(null);
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) =>
      toast.error(err.response?.data?.error?.message || 'Apply failed'),
  });

  return (
    <div className="flex w-96 flex-col border-l bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wider text-gray-500">
            Section Studio
          </p>
          <p className="truncate text-sm font-medium text-gray-900">{sectionId}</p>
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b text-xs">
        <button
          onClick={() => setTab('history')}
          className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 font-medium transition-colors ${
            tab === 'history'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <History className="h-3.5 w-3.5" /> History
        </button>
        <button
          onClick={() => setTab('variants')}
          className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 font-medium transition-colors ${
            tab === 'variants'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Wand2 className="h-3.5 w-3.5" /> AI Variants
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'history' ? (
          <HistoryTab
            versions={versionsQuery.data ?? []}
            isLoading={versionsQuery.isLoading}
            onSnapshot={() => snapshotMutation.mutate(undefined)}
            isSnapshotting={snapshotMutation.isPending}
            onRevert={(id) => revertMutation.mutate(id)}
            revertingId={
              revertMutation.isPending
                ? ((revertMutation.variables as string | undefined) ?? null)
                : null
            }
          />
        ) : (
          <VariantsTab
            batch={batch}
            isGenerating={generateMutation.isPending}
            onGenerate={(n) => generateMutation.mutate(n)}
            onApply={(variantId) => applyMutation.mutate(variantId)}
            applyingId={
              applyMutation.isPending
                ? ((applyMutation.variables as string | undefined) ?? null)
                : null
            }
            onDiscard={() => setBatch(null)}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

function HistoryTab({
  versions,
  isLoading,
  onSnapshot,
  isSnapshotting,
  onRevert,
  revertingId,
}: {
  versions: SectionVersion[];
  isLoading: boolean;
  onSnapshot: () => void;
  isSnapshotting: boolean;
  onRevert: (versionId: string) => void;
  revertingId: string | null;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <span className="text-xs text-gray-500">{versions.length} snapshot(s)</span>
        <button
          onClick={onSnapshot}
          disabled={isSnapshotting}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isSnapshotting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Save className="h-3 w-3" />
          )}
          Save snapshot
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}

      {!isLoading && versions.length === 0 && (
        <p className="px-4 py-8 text-center text-xs text-gray-400">
          No snapshots yet. Save one before making big changes so you can roll back.
        </p>
      )}

      <ul className="divide-y">
        {versions.map((v) => {
          const badge = SOURCE_BADGE[v.source];
          return (
            <li key={v.versionId} className="px-4 py-3 hover:bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                    <span className="flex items-center gap-1 text-[11px] text-gray-400">
                      <Clock className="h-2.5 w-2.5" />
                      {new Date(v.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {v.label && <p className="mt-1 truncate text-xs text-gray-600">{v.label}</p>}
                  <ContentSnippet content={v.content} />
                </div>
                <button
                  onClick={() => onRevert(v.versionId)}
                  disabled={revertingId === v.versionId}
                  className="inline-flex flex-shrink-0 items-center gap-1 rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  title="Revert section to this snapshot"
                >
                  {revertingId === v.versionId ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3 w-3" />
                  )}
                  Revert
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Variants tab
// ---------------------------------------------------------------------------

function VariantsTab({
  batch,
  isGenerating,
  onGenerate,
  onApply,
  applyingId,
  onDiscard,
}: {
  batch: SectionVariantBatch | null;
  isGenerating: boolean;
  onGenerate: (count: number) => void;
  onApply: (variantId: string) => void;
  applyingId: string | null;
  onDiscard: () => void;
}) {
  if (!batch) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
        <Wand2 className="h-6 w-6 text-blue-500" />
        <p className="mt-3 text-sm font-medium text-gray-900">Generate copy variants</p>
        <p className="mt-1 text-xs text-gray-500">
          Get 3 alternate copy directions written by Claude under different stylistic hints
          (editorial, punchy, storytelling). Pick the one you like — the swap is undoable.
        </p>
        <button
          onClick={() => onGenerate(3)}
          disabled={isGenerating}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {isGenerating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          Generate 3 variants
        </button>
        <p className="mt-2 text-[11px] text-gray-400">~$0.005 — uses Haiku 4.5</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <span className="text-xs text-gray-500">
          {batch.variants.length} variants · ${batch.costUsd.toFixed(4)}
        </span>
        <button onClick={onDiscard} className="text-xs text-gray-500 underline hover:text-gray-700">
          Discard
        </button>
      </div>
      <ul className="divide-y">
        {batch.variants.map((v) => (
          <li key={v.variantId} className="px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                  {v.hint.split('—')[0].trim()}
                </p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  {v.hint.includes('—') ? v.hint.split('—')[1].trim() : ''}
                </p>
                <ContentSnippet content={v.content} />
              </div>
              <button
                onClick={() => onApply(v.variantId)}
                disabled={applyingId === v.variantId}
                className="inline-flex flex-shrink-0 items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1.5 text-[11px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {applyingId === v.variantId ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Check className="h-3 w-3" />
                )}
                Use this
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ContentSnippet({ content }: { content: Record<string, unknown> }) {
  const preview = (() => {
    const candidates = ['title', 'heading', 'headline', 'subtitle', 'body', 'description'];
    for (const k of candidates) {
      const v = content[k];
      if (typeof v === 'string' && v.trim()) return v;
    }
    return JSON.stringify(content).slice(0, 140);
  })();

  return (
    <p className="mt-1 line-clamp-2 text-xs text-gray-600" title={preview}>
      {preview}
    </p>
  );
}
